import type { Distance, Rectangle } from "@pdfking/math"
import { CM } from "@pdfking/quantity"
import { NullQ } from "@pdfking/structs"
import { RecordByPrimaryKey } from "@pdfking/templates"
import { SortBy, Values } from "@pdfking/tfunc"

import type { Machine } from "#internal"

// Mirrors Python `machine_list` in `objects/processing/library.py`. Names are
// literal model names (no translation). `min`/`max` mirror the AreaMeasureSet
// bounds used by `infer_machine`.
export const MachineList = RecordByPrimaryKey<Machine>([
  {
    id: "igen",
    name: "Igen",
    min_width: CM(20),
    min_height: CM(20),
    max_width: CM(68),
    max_height: CM(48),
  },
  { id: "igen-p-m", name: "Igen P.M." },
  { id: "iridesse", name: "Iridesse" },
  {
    id: "cd102",
    name: "CD102",
    min_width: CM(70),
    min_height: CM(50),
    max_width: CM(102),
    max_height: CM(72),
  },
  { id: "fresa", name: "Fresa" },
  { id: "durst", name: "Durst" },
  { id: "graphtec", name: "Graphtec" },
  { id: "latex", name: "Latex" },
] as const)

/**
 * Port of Python `infer_machine`: pick the first machine whose size range
 * contains `inner` (orientation-independent), else the last machine.
 */
export const InferMachine = (inner: Rectangle<Distance>): Machine => {
  const [long, short] = SortBy([inner.width, inner.height], d => -d as never)
  const all = Values(MachineList)
  for (const m of all) {
    if (NullQ(m.min_width) || NullQ(m.min_height) || NullQ(m.max_width) || NullQ(m.max_height))
      continue
    const [max_l, max_s] = SortBy([m.max_width, m.max_height], d => -d as never)
    const [min_l, min_s] = SortBy([m.min_width, m.min_height], d => -d as never)
    if (max_l >= long && max_s >= short && long >= min_l && short >= min_s) return m
  }
  return all[all.length - 1]!
}
